import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestYieldCallback, getViewParameterValue } from "@theme/utilities";

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLButtonElement} [applyChangesButton] - The apply changes button (optional).
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLDivElement | null} */
  #swatchTooltip = null;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      this.refs.fieldsets || []
    );

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll("input") ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.dataset.currentChecked === "true",
      );
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    // Preload variant images for instant optimistic swatch switching
    this.#preloadVariantImages();

    // Set up hover tooltip for swatch-style variant buttons
    this.#setupSwatchTooltip();

    // Check if there's an apply button - if so, use deferred updates
    const applyButton = this.refs?.applyChangesButton;
    if (applyButton && !Array.isArray(applyButton)) {
      // Don't auto-update on change, wait for apply button
      this.addEventListener("change", this.#onVariantSelectChange.bind(this));
      applyButton.addEventListener("click", this.#applyChanges.bind(this));
    } else {
      // Original behavior - auto-update on change
      this.addEventListener("change", this.variantChanged.bind(this));
    }
  }

  /**
   * Handles variant select changes when using deferred updates (with apply button).
   * @param {Event} event - The change event.
   */
  #onVariantSelectChange(event) {
    // Just update the visual state of selects, but don't trigger variant update
    if (event.target instanceof HTMLSelectElement) {
      this.updateSelectedOption(event.target);
    }
  }

  /**
   * Applies the pending variant changes when apply button is clicked.
   */
  #applyChanges() {
    // Get all selected options from all selects
    const selects = Array.from(this.querySelectorAll("select"));
    if (selects.length === 0) return;

    // Find a select with a valid selected option to use for building the request
    let selectedOption = null;
    let sourceSelect = null;

    for (const select of selects) {
      const option = select.options[select.selectedIndex];
      if (option && option.dataset.optionValueId) {
        selectedOption = option;
        sourceSelect = select;
        break;
      }
    }

    if (!selectedOption || !sourceSelect) return;

    // Update all selected options visually first
    selects.forEach((select) => {
      this.updateSelectedOption(select);
    });

    // Dispatch the variant selected event with the first option's ID
    // (The actual variant will be determined from all selected options by the server)
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      }),
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !sourceSelect.closest("product-card") &&
      !sourceSelect.closest("quick-add-dialog");

    // Build request URL using all selected options (buildRequestUrl uses selectedOptionsValues)
    const requestUrl = this.buildRequestUrl(selectedOption);
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Store flag to update URL after fetch completes
    this.#shouldUpdateUrl = isOnProductPage;
    this.#pendingNewUrl = loadsNewProduct ? newUrl : null;

    // Fetch the updated section - the response will contain the correct variant ID
    this.fetchUpdatedSection(requestUrl, loadsNewProduct);
  }

  /** @type {boolean} */
  #shouldUpdateUrl = false;

  /** @type {string | null} */
  #pendingNewUrl = null;

  /**
   * Preloads images referenced by swatch inputs so optimistic swaps are instant.
   */
  #preloadVariantImages() {
    this.querySelectorAll("input[data-variant-featured-image]").forEach(
      (input) => {
        const img = new Image();
        img.src = input.dataset.variantFeaturedImage;
      },
    );
  }

  /**
   * Initializes the hover tooltip that follows the cursor for swatch labels.
   * The tooltip is scoped to this variant-picker instance.
   */
  #setupSwatchTooltip() {
    if (this.dataset.swatchTooltipInitialized === "true") return;
    this.dataset.swatchTooltipInitialized = "true";

    const tooltip = document.createElement("div");
    tooltip.className = "swatch-tooltip";
    tooltip.setAttribute("aria-hidden", "true");
    this.appendChild(tooltip);
    this.#swatchTooltip = tooltip;

    /** @param {PointerEvent} event */
    const handlePointerMove = (event) => {
      if (!this.#swatchTooltip?.classList.contains("is-visible")) return;

      const offsetX = 16;
      const offsetY = 20;

      this.#swatchTooltip.style.left = `${event.clientX + offsetX}px`;
      this.#swatchTooltip.style.top = `${event.clientY + offsetY}px`;
    };

    /** @param {PointerEvent} event */
    const handlePointerOver = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !this.#swatchTooltip) return;

      const label = target.closest(".variant-option__button-label--has-swatch");
      if (!label || !this.contains(label)) return;

      const input = /** @type {HTMLInputElement | null} */ (
        label.querySelector('input[type="radio"]')
      );

      const optionName =
        input?.getAttribute("aria-label") || input?.value || "";

      if (!optionName) return;

      this.#swatchTooltip.textContent = optionName;
      this.#swatchTooltip.classList.add("is-visible");
    };

    /** @param {PointerEvent} event */
    const handlePointerOut = (event) => {
      if (!this.#swatchTooltip) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      const related = /** @type {Element | null} */ (event.relatedTarget);

      // If moving between swatch labels inside this picker, keep the tooltip visible
      if (related && this.contains(related)) {
        const relatedLabel = related.closest(
          ".variant-option__button-label--has-swatch",
        );
        if (relatedLabel && this.contains(relatedLabel)) return;
      }

      this.#swatchTooltip.classList.remove("is-visible");
    };

    this.addEventListener("pointermove", handlePointerMove);
    this.addEventListener("pointerover", handlePointerOver);
    this.addEventListener("pointerout", handlePointerOut);
  }

  /**
   * Instantly swaps the main gallery image when a swatch with a known image is clicked.
   * Provides immediate visual feedback while the section fetch is in-flight.
   * The server response will replace the entire gallery with the correct state.
   * @param {HTMLElement} target - The clicked input element.
   */
  #optimisticImageSwap(target) {
    if (!(target instanceof HTMLInputElement)) return;
    const imageUrl = target.dataset.variantFeaturedImage;
    if (!imageUrl) return;

    const gallery =
      this.closest(".shopify-section")?.querySelector("media-gallery");
    if (!gallery) return;

    const mainImg = gallery.querySelector("img");
    if (!mainImg) return;

    // Clear srcset so the browser uses src directly, then swap the image.
    mainImg.removeAttribute("srcset");
    mainImg.src = imageUrl;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.#optimisticImageSwap(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      }),
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      loadsNewProduct,
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
      const inputIndex = Number.parseInt(target.dataset.inputIndex || "");

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
          this.refs.fieldsets || []
        );
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = "false";
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = "false";
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = "true";
            fieldset.style.setProperty(
              "--pill-width-current",
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`,
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = "true";
            radios[newPreviousIndex].dataset.currentChecked = "false";
            fieldset.style.setProperty(
              "--pill-width-previous",
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`,
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest("quick-add-component") ||
      this.closest("swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join(
        "&",
      )}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Use Section Rendering API for single-section updates to avoid
    // fetching the entire page HTML (can be 700KB+ for products with many variants).
    let fetchUrl = requestUrl;
    if (!shouldMorphMain && this.dataset.sectionId) {
      const separator = fetchUrl.includes("?") ? "&" : "?";
      fetchUrl += `${separator}section_id=${this.dataset.sectionId}`;
    }

    fetch(fetchUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`,
        )?.textContent;
        if (!textContent) return;

        const variantData = JSON.parse(textContent);

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // After morphing, get the selected option ID from the updated DOM
          // This ensures we have the correct ID based on all selected options
          const selectedOptionId = this.selectedOptionId;

          // If using apply button, also update the product form component
          if (this.refs?.applyChangesButton) {
            this.#updateProductFormComponent(html);
          }

          // We grab the variant object from the response and dispatch an event with it.
          // The variantData should contain the full variant object with id, available, etc.
          // Use variantData.id as the sourceId if selectedOptionId is not available
          const sourceId =
            selectedOptionId || variantData?.id?.toString() || "";

          if (variantData && sourceId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, sourceId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
              }),
            );
          }
        }

        // Update URL if we're using apply button flow
        if (this.#shouldUpdateUrl && variantData?.id) {
          const url = new URL(window.location.href);
          url.searchParams.set("variant", variantData.id);

          if (this.#pendingNewUrl) {
            url.pathname = this.#pendingNewUrl;
          }

          if (url.href !== window.location.href) {
            requestYieldCallback(() => {
              history.replaceState({}, "", url.toString());
            });
          }

          this.#shouldUpdateUrl = false;
          this.#pendingNewUrl = null;
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Updates the product form component with the new HTML from the section rendering API.
   * @param {Document} newHtml - The new HTML document.
   */
  #updateProductFormComponent(newHtml) {
    // Find the product form component in the current page
    const currentProductForm = this.closest(".shopify-section")?.querySelector(
      "product-form-component",
    );
    if (!currentProductForm) return;

    // Find the product form component in the new HTML
    const newProductForm = newHtml.querySelector("product-form-component");
    if (!newProductForm) return;

    // Morph the product form component to update it with the new variant data
    morph(currentProductForm, newProductForm);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option[selected], fieldset input:checked",
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
